"use strict";
// packages/rdti-schema/src/carry-forward.ts
//
// FY-to-FY mapping for pre-fill when a user starts a new project by carrying
// forward an older one. Only fields with a sensible 1:1 destination are
// mapped — FY25 introduced several questions with no FY24 analogue
// (behavioural-additionality, beneficial ownership, plant & facilities,
// dominant-purpose explanation), and those are intentionally left blank for
// the user to author fresh. Mapping is suggestion-only; never auto-submits.
Object.defineProperty(exports, "__esModule", { value: true });
exports.FY24_TO_FY25 = void 0;
exports.getCarryForwardMap = getCarryForwardMap;
exports.carryForwardResponses = carryForwardResponses;
const registry_1 = require("./registry");
/** FY24 → FY25 mappings. Source key on the left, target key on the right. */
exports.FY24_TO_FY25 = {
    project: {
        'project.objectives': 'project.objectives',
        // FY24 'evidence' narrative maps to the FY25 'documents kept' question.
        'project.evidence': 'project.documentsKept',
        // FY24's project-level hypothesis / technical uncertainty / etc. don't
        // map to a FY25 project field — those concepts moved into Core Activity
        // Q80/Q82/Q89. Carry-forward will instead surface them as suggestions
        // when the user creates the first core activity.
    },
    coreActivity: {
        'core.description': 'core.description',
        'core.hypothesis': 'core.hypothesis',
        'core.technicalUncertainty': 'core.competentProfessional',
        'core.priorKnowledgeGap': 'core.sourcesInvestigated',
        'core.experimentalMethodology': 'core.experiment',
        'core.expectedOutcomes': 'core.evaluation',
        // 'core.scientificPrinciples' has no FY25 destination (folded into Q82/Q83).
        // 'core.evidence' (narrative) is superseded by FY25 Q86 (structured checkbox);
        //   carry-forward leaves Q86 unchecked and surfaces the old text as a hint.
    },
    supportingActivity: {
        'support.description': 'support.description',
        'support.supportDescription': 'support.howSupportsCore',
        'support.dominantPurpose': 'support.dominantPurpose',
        'support.relatedCoreActivityIds': 'support.relatedCoreActivityIds',
        'support.evidence': 'support.evidence',
    },
};
const MAPS = {
    FY24: { FY25: exports.FY24_TO_FY25 },
};
function getCarryForwardMap(fromVersion, toVersion) {
    var _a;
    return (_a = MAPS[fromVersion]) === null || _a === void 0 ? void 0 : _a[toVersion];
}
/**
 * Pre-fill `toResponses` from `fromResponses` using the mapping for the given
 * section. Only copies values where (a) a mapping exists, (b) the target field
 * exists in the target schema, and (c) the target slot is currently empty.
 * Returns the new responses object (does not mutate).
 */
function carryForwardResponses(fromVersion, toVersion, section, fromResponses, toResponses = {}) {
    const map = getCarryForwardMap(fromVersion, toVersion);
    if (!map)
        return Object.assign({}, toResponses);
    const sectionMap = map[section];
    const targetFields = new Set((0, registry_1.getFieldsForSection)(toVersion, section).map((f) => f.key));
    const next = Object.assign({}, toResponses);
    for (const [fromKey, toKey] of Object.entries(sectionMap)) {
        if (!targetFields.has(toKey))
            continue;
        if (next[toKey] !== undefined && next[toKey] !== null && next[toKey] !== '')
            continue;
        const value = fromResponses[fromKey];
        if (value === undefined || value === null || value === '')
            continue;
        // Only carry-forward into compatible target inputTypes — skip when the
        // target is structured (radio / checkbox / currency) but source is text.
        const targetDef = (0, registry_1.findField)(toVersion, section, toKey);
        if (!targetDef)
            continue;
        if (typeof value === 'string' &&
            targetDef.inputType !== 'text' &&
            targetDef.inputType !== 'richText') {
            continue;
        }
        next[toKey] = value;
    }
    return next;
}
